/**
 * Media Upload Dialog
 * 
 * Dialog component for uploading page assets (images and videos) that integrates
 * with the page builder property panel. Handles team-isolated asset storage.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { pageAssetsService, PageAsset, UploadProgress } from '../../services/page-assets.service';

interface MediaUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: PageAsset) => void;
  teamId: string;
  organizationId?: string;
  assetType?: 'image' | 'video' | 'all';
  currentAssetUrl?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

export const MediaUploadDialog: React.FC<MediaUploadDialogProps> = ({
  open,
  onClose,
  onSelect,
  teamId,
  organizationId,
  assetType = 'all',
  currentAssetUrl,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [existingAssets, setExistingAssets] = useState<PageAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Load existing assets when dialog opens
  React.useEffect(() => {
    if (open) {
      loadExistingAssets();
    }
  }, [open, teamId]);

  const loadExistingAssets = async () => {
    try {
      setLoadingAssets(true);
      setError(null);
      
      const filterType = assetType === 'all' ? undefined : assetType;
      const { assets } = await pageAssetsService.listAssets(teamId, { 
        assetType: filterType,
        limit: 50 
      });
      
      setExistingAssets(assets);
    } catch (err) {
      console.error('Failed to load existing assets:', err);
      setError('Failed to load existing assets');
    } finally {
      setLoadingAssets(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    // Validate file type
    if (!pageAssetsService.isValidAssetType(file)) {
      setError('File type not supported. Please upload images (JPEG, PNG, GIF, WebP, SVG) or videos (MP4, WebM, AVI, MOV).');
      return;
    }

    // Validate file size
    if (!pageAssetsService.isValidFileSize(file)) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(null);

      const detectedAssetType = pageAssetsService.getAssetType(file);
      
      // If we're filtering by asset type, ensure the file matches
      if (assetType !== 'all' && detectedAssetType !== assetType) {
        setError(`Please upload a ${assetType} file.`);
        setUploading(false);
        return;
      }

      const asset = await pageAssetsService.uploadAsset(
        file,
        teamId,
        organizationId,
        detectedAssetType,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      // Add to existing assets list
      setExistingAssets(prev => [asset, ...prev]);
      
      // Auto-select the uploaded asset (let parent handle closing)
      onSelect(asset);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }, [teamId, organizationId, assetType, onSelect, onClose]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: assetType === 'image' ? {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']
    } : assetType === 'video' ? {
      'video/*': ['.mp4', '.webm', '.avi', '.mov']
    } : {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg'],
      'video/*': ['.mp4', '.webm', '.avi', '.mov']
    },
    multiple: false,
    disabled: uploading,
  });


  const handleDeleteAsset = async (asset: PageAsset) => {
    try {
      await pageAssetsService.deleteAsset(teamId, asset.id);
      setExistingAssets(prev => prev.filter(a => a.id !== asset.id));
    } catch (err) {
      console.error('Failed to delete asset:', err);
      setError('Failed to delete asset');
    }
  };

  const handleSelectAsset = (asset: PageAsset) => {
    onSelect(asset);
    // Let parent component handle closing
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const resetState = () => {
    setError(null);
    setUploadProgress(null);
    setUploading(false);
    setTabValue(0);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const isImageAsset = (asset: PageAsset) => asset.mimeType.startsWith('image/');
  const isVideoAsset = (asset: PageAsset) => asset.mimeType.startsWith('video/');

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Select Media Asset
            {assetType !== 'all' && <Chip label={assetType} size="small" sx={{ ml: 1 }} />}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Upload New" />
          <Tab label="Existing Assets" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Upload Tab */}
          <Box
            {...getRootProps()}
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: isDragActive ? 'primary.light' : 'background.paper',
              cursor: uploading ? 'default' : 'pointer',
              opacity: uploading ? 0.6 : 1,
              transition: 'all 0.2s ease',
              '&:hover': uploading ? {} : {
                borderColor: 'primary.main',
                bgcolor: 'primary.light'
              }
            }}
          >
            <input {...getInputProps()} />
            
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              {uploading ? 'Uploading...' : 'Click here to select files or drag & drop'}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {assetType === 'image' && 'Supported formats: JPEG, PNG, GIF, WebP, SVG'}
              {assetType === 'video' && 'Supported formats: MP4, WebM, AVI, MOV'}
              {assetType === 'all' && 'Supported formats: Images (JPEG, PNG, GIF, WebP, SVG) and Videos (MP4, WebM, AVI, MOV)'}
            </Typography>
            
            <Typography variant="caption" color="text.secondary">
              Maximum file size: 50MB
            </Typography>

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress 
                  variant={uploadProgress ? "determinate" : "indeterminate"}
                  value={uploadProgress?.percentage || 0}
                />
                {uploadProgress && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {uploadProgress.percentage}% • {pageAssetsService.formatFileSize(uploadProgress.loaded)} / {pageAssetsService.formatFileSize(uploadProgress.total)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Existing Assets Tab */}
          {loadingAssets ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading assets...
              </Typography>
            </Box>
          ) : existingAssets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No assets found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload your first asset using the "Upload New" tab
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ maxHeight: 400, overflow: 'auto' }}>
              {existingAssets.map((asset) => (
                <Grid item xs={12} sm={6} md={4} key={asset.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 aspect ratio */ }}>
                      {isImageAsset(asset) ? (
                        <CardMedia
                          component="img"
                          image={asset.url}
                          alt={asset.originalName}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : isVideoAsset(asset) ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100'
                          }}
                        >
                          <VideoIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100'
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                        </Box>
                      )}
                    </Box>
                    
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      <Typography variant="body2" noWrap title={asset.originalName}>
                        {asset.originalName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pageAssetsService.formatFileSize(asset.size)}
                      </Typography>
                    </CardContent>
                    
                    <CardActions sx={{ pt: 0 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelectAsset(asset)}
                        sx={{ flexGrow: 1 }}
                      >
                        Select
                      </Button>
                      <Tooltip title="Delete asset">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAsset(asset)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};